// src/components/SecMasterGrid.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  History as HistoryIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ContentCopy as DuplicateIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  FilterAlt as FilterIcon,
  ArrowDropDown as ArrowDropDownIcon
} from "@mui/icons-material";

import api,
  { fetchSecMaster,
  saveSecMaster,
  deleteSecMaster,
  addUserAction } from "../api";
import config from "../data/config.json";
import {
  PamGrid,
  useGridConfigs,
  GridConfigMenu,
  ConfirmationDialog,
  useNotification,
  ValidationErrorModal,
  HistoryModal,
  parseFileData,
  buildHeaderMappingsFromAliases,
  createColumnNormalizer,
  normalizeRowKeys,
  mergeRows,
  createChoicesValidator,
  notifyPermissionError,
  getWriteErrorMessage,
  nullToEmpty,
  VERSION_CONFLICT_MESSAGE,
} from '@1823-partners/core';
import SecurityModal from "./SecurityModal";
import SecMasterHistory from "./SecMasterHistory";
import { formatDate, formatDateTimeForDisplay } from "../utils";
import { formatSecMasterPayload, isReadOnlySecMaster, isCreOnlyField, isSyntheticCusipPpn, isValueNull, pickImportFields, validateSecMasterList, getNaicDesignationFromRating, getCollateralTypeFromAssetType } from "../utils/secMasterUtils";
import { isStaleWrite, tokensByKey } from "../utils/modifiedDates";
import { beforeForLocalDay } from "../utils/asOf";
import secMasterFields from "../data/secMasterFields.json";

// Build mappings dynamically from field definitions
const CLEARWATER_COLUMN_MAPPING = Object.entries(secMasterFields)
  .filter(([key, field]) => field.clearwater_name)
  .reduce((acc, [key, field]) => {
    acc[key] = field.clearwater_name;
    return acc;
  }, {});

// Build 1823-core alias map for import: clearwater_name → internal field name
// Also includes direct field name as alias for fields without a clearwater_name
const CW_IMPORT_ALIASES = Object.entries(secMasterFields).reduce((acc, [key, field]) => {
  const aliases = [key]; // Always accept the internal field name directly
  if (field.clearwater_name) {
    aliases.push(field.clearwater_name);
  }
  acc[key] = aliases;
  return acc;
}, {});
const CW_IMPORT_MAPPINGS = buildHeaderMappingsFromAliases(CW_IMPORT_ALIASES);
const cwColumnNormalizer = createColumnNormalizer(CW_IMPORT_MAPPINGS);

// Required clearwater columns for upload validation
const REQUIRED_CW_COLS = new Set(
  Object.entries(secMasterFields)
    .filter(([, field]) => field.clearwater_name)
    .map(([, field]) => field.clearwater_name)
);

const MLX_COLUMN_MAPPING = Object.entries(secMasterFields)
  .filter(([key, field]) => field.mlx_name)
  .reduce((acc, [key, field]) => {
    acc[key] = field.mlx_name;
    return acc;
  }, {});

// Build per-field choices validators from secMasterFields definitions
const choicesValidatorsByField = Object.entries(secMasterFields)
  .filter(([, def]) => def && def.choices && Array.isArray(def.choices))
  .reduce((acc, [field, def]) => {
    acc[field] = createChoicesValidator(field, def.choices);
    return acc;
  }, {});

const MLX_COLUMN_ORDER = config.mlx_column_order;

const READ_ONLY_MESSAGE = 'CRE loans are read-only here; edit them in the CRE app.';

const MLX_DEFAULTS = config.mlx_defaults;

const SecMasterGrid = ({ userId }) => {
  const [editingSecMaster, setEditingSecMaster] = useState(null);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);
  const [selectedSecurityType, setSelectedSecurityType] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedSecMasterForHistory, setSelectedSecMasterForHistory] = useState(null);
  const [newRowCount, setNewRowCount] = useState(0);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [secMasterToDelete, setSecMasterToDelete] = useState(null);
  const [validationErrorModalOpen, setValidationErrorModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [updatedRows, setUpdatedRows] = useState({}); // Dictionary to track rows with updated cells
  // The rows on screen, for checks inside stable callbacks (e.g. a CUSIP rename clash).
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Default to today
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [addSecurityMenuAnchor, setAddSecurityMenuAnchor] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedForExport, setSelectedForExport] = useState(new Set()); // Track which rows are selected for export
  const theme = useTheme();
  const { showNotification } = useNotification();
  const importFileInputRef = useRef(null);
  const gridRef = useRef(null);
  // CUSIP_PPN -> MODIFIED_DATE exactly as last loaded: the token each save sends.
  // Rows with unsaved edits keep the token those edits were based on.
  const loadedTokens = useRef(new Map());
  const loadedSnapshots = useRef(new Map());
  // CUSIP_PPN -> the row exactly as the server sent it (NULLs intact), kept in lockstep with
  // loadedSnapshots, so a save can put back blanks the user never touched.
  const loadedRaw = useRef(new Map());
  const [conflictReview, setConflictReview] = useState(null);
  const isHistorical = !selectedDate?.isSame(dayjs(), 'day');
  const editingBlocked = isHistorical || Boolean(conflictReview);
  // Event handlers also guard writes, including callbacks retained by open editors.
  const writeBlockedRef = useRef(editingBlocked);
  writeBlockedRef.current = editingBlocked;


  // Per-user grid layout persistence (column order/width/visibility/sort/pinning
  // + filter model). Owning the hook here lets us wire gridConfigs.onGridReady to
  // PamGrid's onGridReady so a slow-mounting grid still gets the default applied.
  const gridConfigs = useGridConfigs(api, {
    app: 'secmaster',
    gridId: 'securities',
    gridApiRef: gridRef,
  });

  // A 409 may refresh the display, but pending edits retain their old tokens
  // until the user explicitly resolves the comparison with the server values.
  const loadSecMaster = useCallback(async (preserveLocalChanges = false, silent = false, reviewConflict = false) => {
    try {
      if (!silent) {
        setIsLoading(true);
      }

      const response = await fetchSecMaster(beforeForLocalDay(selectedDate), selectedDate.format('YYYY-MM-DD'));
      // Before the null -> '' display formatting below, so a NULL stays null.
      const freshTokens = tokensByKey(response.data, 'CUSIP_PPN');
      const freshRaw = new Map(response.data.map(row => [row.CUSIP_PPN, row]));
      const previousTokens = loadedTokens.current;
      const formattedData = response.data.map(secMaster => {
        // Convert all null values to empty strings
        const formattedSecMaster = nullToEmpty(secMaster);
        // Add specific formatting for date fields
        formattedSecMaster.TRADE_DATE = formatDate(formattedSecMaster.TRADE_DATE);
        formattedSecMaster.SETTLE_DATE = formatDate(formattedSecMaster.SETTLE_DATE);
        formattedSecMaster.MATURITY_DATE = formatDate(formattedSecMaster.MATURITY_DATE);
        formattedSecMaster.FIRST_COUPON_DATE = formatDate(formattedSecMaster.FIRST_COUPON_DATE);
        formattedSecMaster.RATING_DATE = formatDate(formattedSecMaster.RATING_DATE);
        formattedSecMaster.ACCRUED_INTEREST_START_DATE = formatDate(formattedSecMaster.ACCRUED_INTEREST_START_DATE);
        // LAST_EXPORT_DATE is a real timestamp (date+time); leave it raw so the
        // column's formatDateTimeForDisplay renders local date+time. Eager
        // formatDate here would strip the time and day-shift evening values.
        return formattedSecMaster;
      });

      // AG Grid can mutate displayed row objects. Keep independent snapshots
      // alongside their version tokens for a reliable three-way comparison.
      const freshSnapshots = new Map(formattedData.map(row => [row.CUSIP_PPN, { ...row }]));

      if (preserveLocalChanges) {
        // Fields that should always use server data (cannot be edited by user).
        // NOTE: MODIFIED_BY and MODIFIED_DATE are intentionally excluded here so that
        // locally-tracked edits (stored in updatedRows) survive a preserveLocalChanges reload.
        const serverOnlyFields = ['LAST_EXPORT_USER', 'LAST_EXPORT_DATE'];

        // Capture current state values and calculate final values
        setUpdatedRows(currentUpdatedRows => {
          const tokens = new Map(freshTokens);
          const snapshots = new Map(freshSnapshots);
          const raws = new Map(freshRaw);
          Object.keys(currentUpdatedRows).forEach(key => {
            if (previousTokens.has(key)) tokens.set(key, previousTokens.get(key));
            if (loadedSnapshots.current.has(key)) snapshots.set(key, loadedSnapshots.current.get(key));
            if (loadedRaw.current.has(key)) raws.set(key, loadedRaw.current.get(key));
          });
          if (reviewConflict && Object.keys(currentUpdatedRows).length) {
            const comparisons = Object.entries(currentUpdatedRows).flatMap(([key, changes]) => {
              const serverRow = formattedData.find(row => row.CUSIP_PPN === key);
              return Object.entries(changes)
                .filter(([field]) => field !== 'created' && !serverOnlyFields.includes(field))
                .map(([field, change]) => ({
                  key, field, original: loadedSnapshots.current.get(key)?.[field], local: change.newValue,
                  server: serverRow?.[field],
                }));
            });
            setConflictReview({ rows: formattedData, tokens: freshTokens, raws: freshRaw, comparisons,
              missing: Object.entries(currentUpdatedRows).some(([key, changes]) => changes.created || !freshTokens.has(key)) });
          }
          loadedTokens.current = tokens;
          loadedSnapshots.current = snapshots;
          loadedRaw.current = raws;
          setRowData(currentRowData => {
            // If preserving local changes, merge them back into the fresh data
            let finalData = formattedData;

            if (Object.keys(currentUpdatedRows).length > 0 || currentRowData.some(row => isSyntheticCusipPpn(row.CUSIP_PPN))) {
              // First, apply unsaved changes to existing server data
              const updatedServerData = formattedData.map(row => {
                const rowChanges = currentUpdatedRows[row.CUSIP_PPN];
                if (rowChanges) {
                  // Apply unsaved changes to this row, but always use server data for server-only fields
                  const modifiedRow = { ...row };
                  Object.keys(rowChanges).forEach(field => {
                    // Always use server data for export fields and system fields
                    if (!serverOnlyFields.includes(field)) {
                      modifiedRow[field] = rowChanges[field].newValue;
                    }
                  });
                  return modifiedRow;
                }
                return row;
              });

              // Collect all new rows that need to be preserved
              const preservedNewRows = [];
              currentRowData.forEach(existingRow => {
                // Unsaved rows: synthetic ids, and created rows that already carry their
                // real CUSIP/PPN (renamed duplicates, imported new securities).
                if (isSyntheticCusipPpn(existingRow.CUSIP_PPN) || currentUpdatedRows[existingRow.CUSIP_PPN]?.created) {
                  // Check if this new row is already in the fresh data
                  const existsInFreshData = updatedServerData.some(row => row.CUSIP_PPN === existingRow.CUSIP_PPN);
                  if (!existsInFreshData) {
                    preservedNewRows.push(existingRow);
                  }
                }
              });

              // Put new rows at the top, followed by server data
              finalData = [...preservedNewRows, ...updatedServerData];
            }

            return finalData;
          });


          // Return the preserved tracking state
          return currentUpdatedRows;
        });
      } else {
        loadedTokens.current = freshTokens;
        loadedSnapshots.current = freshSnapshots;
        loadedRaw.current = freshRaw;
        setRowData(formattedData);
        setUpdatedRows({});
      }
    } catch (error) {
      console.error("Error fetching sec master:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to load sec master data. Please try again.", "error");
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [selectedDate, showNotification]);

  const resolveConflict = (keepLocal) => {
    if (!conflictReview || (keepLocal && conflictReview.missing)) return;
    loadedTokens.current = conflictReview.tokens;
    loadedSnapshots.current = new Map(conflictReview.rows.map(row => [row.CUSIP_PPN, { ...row }]));
    loadedRaw.current = conflictReview.raws;
    if (keepLocal) {
      setUpdatedRows(current => Object.fromEntries(Object.entries(current).map(([key, changes]) => {
        const serverRow = conflictReview.rows.find(row => row.CUSIP_PPN === key);
        return [key, Object.fromEntries(Object.entries(changes).map(([field, change]) =>
          [field, field === 'created' ? change : { ...change, oldValue: serverRow?.[field] }]))];
      })));
    } else {
      setRowData(conflictReview.rows);
      setUpdatedRows({});
    }
    setConflictReview(null);
  };

  // Load data on component mount with default date
  useEffect(() => {
    loadSecMaster();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Reload data when date changes
  useEffect(() => {
    if (selectedDate) {
      loadSecMaster();
    }
  }, [selectedDate, loadSecMaster]);


  // Auto-size columns after data loads and when loading completes
  useEffect(() => {
    if (rowData.length > 0 && !isLoading && gridRef.current && gridRef.current.api) {
      // Use requestAnimationFrame to ensure grid has rendered before auto-sizing
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (gridRef.current && gridRef.current.api) {
            // Get all column IDs to auto-size with header consideration
            const columns = gridRef.current.api.getColumns();
            if (columns && columns.length > 0) {
              const columnIds = columns.map(col => col.getColId());
              // skipHeader: false means include header width in calculation
              gridRef.current.api.autoSizeColumns(columnIds, false);
            } else {
              // Fallback to autoSizeAllColumns if columns aren't available yet
              gridRef.current.api.autoSizeAllColumns();
            }
          }
        }, 100);
      });
    }
  }, [rowData, isLoading]);

  const handleEditSecMaster = useCallback((secMasterData) => {
    if (writeBlockedRef.current) return false;
    // Do not allow editing if SECURITY_OWNER is CRE
    if (secMasterData && secMasterData.SECURITY_OWNER === 'CRE') {
      showNotification('Cannot edit entries with SECURITY_OWNER = CRE', 'warning');
      return;
    }
    // Get the current row data from rowData state to include any grid edits
    const currentRowData = rowData.find(row => row.CUSIP_PPN === secMasterData.CUSIP_PPN);
    // Use current row data if found, otherwise fall back to the passed data
    const dataToEdit = currentRowData || secMasterData;
    setEditingSecMaster(dataToEdit);
    setSecurityModalOpen(true);
  }, [rowData, showNotification]);

  // The MODIFIED_DATE a row was loaded with; null for a row that is new here
  // (a temp key, or a created upload/duplicate), even if its key matches a loaded one.
  const tokenFor = useCallback((row) => {
    const key = row?.CUSIP_PPN;
    if (!key || key.startsWith('new_row_') || updatedRows[key]?.created) return null;
    return loadedTokens.current.get(key) ?? null;
  }, [updatedRows]);

  // What an existing row looked like when loaded, for putting back blanks the user did
  // not touch; null for a new row (a temp key, or a created upload/duplicate).
  // `alsoTouched` names fields changed outside grid edit tracking (the modal's form,
  // including the COLLATERAL_TYPE / NAIC_DESIGNATION it derives).
  const baselineFor = useCallback((row, alsoTouched = {}) => {
    const key = row?.CUSIP_PPN;
    if (!key || key.startsWith('new_row_') || updatedRows[key]?.created || !loadedRaw.current.has(key)) return null;
    return {
      raw: loadedRaw.current.get(key),
      display: loadedSnapshots.current.get(key),
      touched: { ...updatedRows[key], ...alsoTouched },
    };
  }, [updatedRows]);

  // A failed write: 403 → permission hint; 409 → the conflict message and a reload
  // (pending edits require explicit resolution); otherwise the response error.
  const notifySaveError = useCallback(async (error, fallback) => {
    if (notifyPermissionError(showNotification, error)) return;
    if (isStaleWrite(error)) {
      showNotification(getWriteErrorMessage(error, VERSION_CONFLICT_MESSAGE), 'warning');
      await loadSecMaster(true, true, true);
      return;
    }
    showNotification(getWriteErrorMessage(error, fallback), 'error');
  }, [showNotification, loadSecMaster]);

  const handleSaveEditedSecMaster = useCallback(async (editedData) => {
    if (writeBlockedRef.current) return false;
    if (isReadOnlySecMaster(editedData)) {
      showNotification(READ_ONLY_MESSAGE, 'warning');
      return false;
    }
    // A duplicated row still carries its synthetic new_row_* id and has no stored
    // row: posting it would save new_row_* as a CUSIP. Keep the form's edits on the
    // local row instead; the grid Save creates it once the real CUSIP/PPN is entered.
    if (isSyntheticCusipPpn(editedData.CUSIP_PPN)) {
      const key = editedData.CUSIP_PPN;
      const localRow = rowData.find(row => row.CUSIP_PPN === key);
      const timestamp = new Date().toISOString();
      const changes = Object.fromEntries(
        Object.keys(editedData)
          .filter(field => field !== 'CUSIP_PPN' && localRow?.[field] !== editedData[field])
          .map(field => [field, { oldValue: localRow?.[field], newValue: editedData[field], timestamp }])
      );
      setRowData(prev => prev.map(row => (row.CUSIP_PPN === key ? { ...row, ...editedData } : row)));
      setUpdatedRows(prev => ({ ...prev, [key]: { ...prev[key], ...changes } }));
      showNotification('Changes applied to the new row. Enter its CUSIP/PPN in the grid, then Save to create it.', 'info');
      return true;
    }
    try {
      setIsLoading(true);

      // Merge edited data with existing row data to preserve fields not in modal
      const existingRow = rowData.find(row => row.CUSIP_PPN === editedData.CUSIP_PPN);
      const mergedData = existingRow ? { ...existingRow, ...editedData } : editedData;

      // The modal's edits are not in updatedRows. Every field it returned with a
      // value other than the row's on-screen one is an explicit edit, derived
      // fields included: an unmapped ASSET_TYPE deriving COLLATERAL_TYPE null must
      // not be restored to a loaded ''.
      const modalTouched = Object.fromEntries(
        Object.keys(editedData)
          .filter(field => !existingRow || existingRow[field] !== editedData[field])
          .map(field => [field, true])
      );

      // Call the save-entry endpoint with the loaded MODIFIED_DATE
      await saveSecMaster(formatSecMasterPayload([mergedData], tokenFor, row => baselineFor(row, modalTouched)));

      // The modal saved this row, including any edits previously tracked by the
      // grid. Keep other unsaved rows, but do not overlay this row's old edits
      // or old token onto the reload.
      setUpdatedRows(current => {
        const remaining = { ...current };
        delete remaining[mergedData.CUSIP_PPN];
        return remaining;
      });
      await loadSecMaster(true, true);

      showNotification('Sec Master entry saved successfully', 'success');
      return true;
    } catch (error) {
      console.error('Error saving sec master entry:', error);
      await notifySaveError(error, 'Failed to save sec master entry');
      return isStaleWrite(error) ? 'conflict' : false;
    } finally {
      setIsLoading(false);
    }
  }, [rowData, showNotification, loadSecMaster, notifySaveError, tokenFor, baselineFor]);

  const handleSaveNewSecurity = useCallback(async (newSecurityData) => {
    if (writeBlockedRef.current) return false;
    if (isReadOnlySecMaster(newSecurityData)) {
      showNotification(READ_ONLY_MESSAGE, 'warning');
      return false;
    }
    // Never create a security under the grid's placeholder id.
    if (isSyntheticCusipPpn(newSecurityData?.CUSIP_PPN)) {
      showNotification('Enter the real CUSIP/PPN before creating the security.', 'warning');
      return false;
    }
    try {
      setIsLoading(true);

      // A brand-new security sends a null MODIFIED_DATE (an existing CUSIP_PPN will 409)
      await saveSecMaster(formatSecMasterPayload([newSecurityData]));

      // Reload the grid to get the latest data from the server
      await loadSecMaster(true, true);

      showNotification('New security entry created successfully', 'success');
      return true;
    } catch (error) {
      console.error('Error creating sec master entry:', error);
      await notifySaveError(error, 'Failed to create sec master entry');
      return isStaleWrite(error) ? 'conflict' : false;
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, loadSecMaster, notifySaveError]);

  const handleViewHistory = useCallback((secMasterData) => {
    setSelectedSecMasterForHistory(secMasterData);
    setHistoryModalOpen(true);
  }, []);

  const handleDeleteSecMaster = useCallback((secMasterData) => {
    if (writeBlockedRef.current) return false;
    if (secMasterData.CUSIP_PPN.startsWith('new_row_')) {
      // If it's a new row that hasn't been saved yet, just remove it from the local state
      setRowData(prev => prev.filter(row => row !== secMasterData));
      // Also remove from updated rows tracking
      setUpdatedRows(prev => {
        const updated = { ...prev };
        delete updated[secMasterData.CUSIP_PPN];
        return updated;
      });
      return;
    }

    setSecMasterToDelete(secMasterData);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDeleteSecMaster = useCallback(async () => {
    if (writeBlockedRef.current) return false;
    if (!secMasterToDelete) return;
    if (isReadOnlySecMaster(secMasterToDelete)) {
      showNotification(READ_ONLY_MESSAGE, 'warning');
      setDeleteDialogOpen(false);
      setSecMasterToDelete(null);
      return;
    }

    try {
      // A delete appends a tombstone; it carries the loaded MODIFIED_DATE and the
      // writer stamps MODIFIED_BY / MODIFIED_DATE.
      await deleteSecMaster(formatSecMasterPayload([secMasterToDelete], tokenFor, baselineFor));

      // Refresh the data after successful deletion, preserve any unsaved changes
      await loadSecMaster(true);
      showNotification('Sec Master entry deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting sec master entry:', error);
      await notifySaveError(error, 'Failed to delete sec master entry');
    } finally {
      // Close the dialog and reset state
      setDeleteDialogOpen(false);
      setSecMasterToDelete(null);
    }
  }, [secMasterToDelete, loadSecMaster, showNotification, notifySaveError, tokenFor, baselineFor]);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setSecMasterToDelete(null);
  }, []);

  const handleCloseValidationErrorModal = useCallback(() => {
    setValidationErrorModalOpen(false);
    setValidationErrors([]);
  }, []);

  const handleDuplicateSecMaster = useCallback((secMasterData) => {
    if (writeBlockedRef.current) return false;
    const nextRowCount = newRowCount + 1;
    setNewRowCount(nextRowCount);

    // Create a new entry with only specific fields copied
    const duplicatedSecMaster = {
      CUSIP_PPN: `new_row_${nextRowCount}`,
      SECURITY_DESCRIPTION: secMasterData.SECURITY_DESCRIPTION,
      SECURITY_OWNER: secMasterData.SECURITY_OWNER,
      TICKER: secMasterData.TICKER,
      ISSUER: secMasterData.ISSUER,
      ASSET_TYPE: secMasterData.ASSET_TYPE,
      SECURITY_TYPE: secMasterData.SECURITY_TYPE,
      CURRENCY: secMasterData.CURRENCY,
      STATUS: secMasterData.STATUS,
      TRADE_DATE: secMasterData.TRADE_DATE,
      SETTLE_DATE: secMasterData.SETTLE_DATE,
      MATURITY_DATE: secMasterData.MATURITY_DATE,
      TRADE_PRICE: secMasterData.TRADE_PRICE,
      STAT_BOOK_VALUE: secMasterData.STAT_BOOK_VALUE,
      STAT_CURRENT_UNITS: secMasterData.STAT_CURRENT_UNITS,
      TOTAL_COMMITMENT: secMasterData.TOTAL_COMMITMENT,
      COUPON_TYPE: secMasterData.COUPON_TYPE,
      COUPON_FREQUENCY: secMasterData.COUPON_FREQUENCY,
      ALL_IN_RATE: secMasterData.ALL_IN_RATE,
      BASE_RATE: secMasterData.BASE_RATE,
      STAT_BOOK_YIELD: secMasterData.STAT_BOOK_YIELD,
      AMORTIZATION_TYPE: secMasterData.AMORTIZATION_TYPE,
      COUNTRY_OF_ISSUE: secMasterData.COUNTRY_OF_ISSUE,
      RATING: secMasterData.RATING,
      RATING_AGENCY: secMasterData.RATING_AGENCY,
      COMMENTS: secMasterData.COMMENTS
    };
    // CRE-only loan fields (BORROWER, SERVICE_FEE, ...) are not copied: SEC_MASTER
    // cannot store them, so the duplicate would show values that never save.

    // Initialize all other fields as empty strings
    const allFields = [
      "CUSIP_PPN", "SECURITY_DESCRIPTION", "SECURITY_OWNER", "TICKER", "ISSUER",
      "ASSET_TYPE", "SECURITY_TYPE", "CURRENCY", "STATUS", "TRADE_DATE", "SETTLE_DATE",
      "MATURITY_DATE", "TRADE_PRICE", "STAT_BOOK_VALUE", "STAT_CURRENT_UNITS", "TOTAL_COMMITMENT",
      "COUPON_TYPE", "COUPON_FREQUENCY", "ALL_IN_RATE", "BASE_RATE", "STAT_BOOK_YIELD",
      "AMORTIZATION_TYPE", "COUNTRY_OF_ISSUE", "RATING", "RATING_AGENCY", "COMMENTS"
    ];

    allFields.forEach(field => {
      if (!duplicatedSecMaster.hasOwnProperty(field)) {
        duplicatedSecMaster[field] = "";
      }
    });

    // Add the duplicated row at the top of the data
    setRowData(prev => [duplicatedSecMaster, ...prev]);

    // Track the new row as modified so it can be saved
    setUpdatedRows(prev => ({
      ...prev,
      [`new_row_${nextRowCount}`]: {
        created: {
          oldValue: null,
          newValue: 'new_row',
          timestamp: new Date().toISOString()
        }
      }
    }));

    showNotification('Sec Master entry duplicated successfully with selected fields', 'success');
  }, [newRowCount, showNotification]);

  const handleImportSecMaster = useCallback(() => {
    if (writeBlockedRef.current) return false;
    importFileInputRef.current?.click();
  }, []);


  // Function to transform data for Clearwater export
  const transformDataForClearwater = useCallback((data) => {
    return data.map(row => {
      const transformedRow = { ...row };

      // Transform Modified By field - remove everything after @
      if (transformedRow.MODIFIED_BY && transformedRow.MODIFIED_BY.includes('@')) {
        transformedRow.MODIFIED_BY = transformedRow.MODIFIED_BY.substring(0, transformedRow.MODIFIED_BY.indexOf('@'));
      }

      // Transform PIK Interest field - convert 'Cash' to 'N', others to 'Y'
      if (transformedRow.PIK_INTEREST) {
        transformedRow.PIK_INTEREST = transformedRow.PIK_INTEREST === 'Cash' ? 'N' : 'Y';
      }

      return transformedRow;
    });
  }, []);

  // Function to apply Clearwater column mapping
  const applyClearwaterMapping = useCallback((data) => {
    return data.map(row => {
      const mappedRow = {};

      // Apply column mapping
      Object.keys(CLEARWATER_COLUMN_MAPPING).forEach(originalKey => {
        const clearwaterKey = CLEARWATER_COLUMN_MAPPING[originalKey];
        if (row[originalKey] !== undefined) {
          mappedRow[clearwaterKey] = row[originalKey];
        }
      });

      return mappedRow;
    });
  }, []);

  // Function to transform data for MLx export
  const transformDataForMLx = useCallback((data) => {
    return data.map(row => {
      const transformedRow = { ...row };

      // Transform Modified By field - remove everything after @
      if (transformedRow.MODIFIED_BY && transformedRow.MODIFIED_BY.includes('@')) {
        transformedRow.MODIFIED_BY = transformedRow.MODIFIED_BY.substring(0, transformedRow.MODIFIED_BY.indexOf('@'));
      }

      return transformedRow;
    });
  }, []);

  // Function to apply MLx column mapping
  const applyMLxMapping = useCallback((data) => {
    return data.map(row => {
      const mappedRow = {};

      // Apply column mapping
      Object.keys(MLX_COLUMN_MAPPING).forEach(originalKey => {
        const mlxKey = MLX_COLUMN_MAPPING[originalKey];
        if (row[originalKey] !== undefined) {
          mappedRow[mlxKey] = row[originalKey];
        }
      });

      return mappedRow;
    });
  }, []);

  // Function to calculate penultimate coupon date
  const calcPenultimateCouponDate = useCallback((maturityDate, payDayValue) => {
    if (!maturityDate) return '';

    const maturity = dayjs(maturityDate);
    const penultimate = maturity.subtract(1, 'month');

    // Get the last day of the penultimate month
    const lastDay = penultimate.daysInMonth();

    // Use payDayValue if it's within the month, otherwise use the last day
    const day = payDayValue <= lastDay ? payDayValue : lastDay;

    return penultimate.date(day).format('YYYY-MM-DD');
  }, []);

  // Function to apply MLx calculations and defaults
  const applyMLxCalculationsAndDefaults = useCallback((data) => {
    return data.map(row => {
      const processedRow = { ...row };

      // Calculate originalAmortizationTerm
      if (processedRow.originalLoanTerm && processedRow.interestOnlyTerm) {
        processedRow.originalAmortizationTerm = processedRow.originalLoanTerm - processedRow.interestOnlyTerm;
      }

      // Calculate payDayCalculationValue from firstCouponDate
      if (processedRow.firstCouponDate) {
        const firstCoupon = dayjs(processedRow.firstCouponDate);
        processedRow.payDayCalculationValue = firstCoupon.date();
      }

      // Calculate penultimateCouponDate
      if (processedRow.parentMaturityDate && processedRow.payDayCalculationValue) {
        processedRow.penultimateCouponDate = calcPenultimateCouponDate(
          processedRow.parentMaturityDate,
          processedRow.payDayCalculationValue
        );
      }

      // Apply defaults for missing columns
      MLX_COLUMN_ORDER.forEach(col => {
        if (!processedRow[col]) {
          const defaultValue = MLX_DEFAULTS[col];
          if (defaultValue && processedRow[defaultValue]) {
            processedRow[col] = processedRow[defaultValue];
          } else if (defaultValue) {
            processedRow[col] = defaultValue;
          } else {
            processedRow[col] = '';
          }
        }
      });

      return processedRow;
    });
  }, [calcPenultimateCouponDate]);

  // Function to reorder columns according to MLx order
  const reorderMLxColumns = useCallback((data) => {
    return data.map(row => {
      const orderedRow = {};
      MLX_COLUMN_ORDER.forEach(col => {
        orderedRow[col] = row[col] || '';
      });
      return orderedRow;
    });
  }, []);

  // Custom loading overlay component
  const CustomLoadingOverlay = useCallback((props) => {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: theme.palette.background.paper,
        zIndex: 1000
      }}>
        <CircularProgress size={40} />
        <div style={{ marginTop: '16px', fontSize: '16px', fontWeight: 'bold' }}>
          {props.message || 'Loading...'}
        </div>
      </div>
    );
  }, [theme]);

  // Register custom loading overlay component
  useEffect(() => {
    if (window.agGrid && window.agGrid.Component) {
      window.agGrid.Component.register('customLoadingOverlay', CustomLoadingOverlay);
    }
  }, [CustomLoadingOverlay]);

  const excelStyles = useMemo(() => [
    {
      id: 'issueDate',
      dataType: 'DateTime',
      numberFormat: {
        format: 'yyyy-mm-dd'
      }
    },
    {
      id: 'maturityDate',
      dataType: 'DateTime',
      numberFormat: {
        format: 'yyyy-mm-dd'
      }
    }
  ], []);

  const handleExportClearwater = useCallback(async () => {
    try {
      if (!gridRef.current || !gridRef.current.api) {
        showNotification('Grid not ready for export', 'error');
        return;
      }

      setIsExporting(true);
      setExportMenuAnchor(null);

      // Get only selected rows for export
      const selectedRowData = rowData.filter(row => selectedForExport.has(row.CUSIP_PPN));

      if (selectedRowData.length === 0) {
        showNotification('Please select at least one row to export', 'warning');
        setIsExporting(false);
        return;
      }

      // Transform data for Clearwater
      const transformedData = transformDataForClearwater(selectedRowData);

      // Apply column mapping
      const mappedData = applyClearwaterMapping(transformedData);

      // Get the mapped column keys for export
      const clearwaterColumns = Object.values(CLEARWATER_COLUMN_MAPPING);

      // Store original column definitions
      const originalColumnDefs = gridRef.current.api.getColumnDefs();

      // Create new column definitions for Clearwater export
      const clearwaterColumnDefs = clearwaterColumns.map(col => ({
        field: col,
        headerName: col,
        width: 150
      }));

      // Temporarily update grid with mapped data and new column definitions
      gridRef.current.api.setGridOption('columnDefs', clearwaterColumnDefs);
      gridRef.current.api.setGridOption('rowData', mappedData);

      // Wait for grid to update before exporting
      setTimeout(() => {
        // Export with the new data
        const params = {
          fileName: `sec_master_clearwater_${dayjs().format('YYYY-MM-DD')}.xlsx`,
          sheetName: 'Sec Master Entries',
          exportMode: 'xlsx',
          skipColumnHeaders: false,
          skipColumnGroupHeaders: false,
          skipPinnedTop: false,
          skipPinnedBottom: false,
          allColumns: true,
          onlySelected: false
        };

        gridRef.current.api.exportDataAsExcel(params);
      }, 100);

      // Add user actions for exported CUSIPs
      try {
        const cusips = selectedRowData.map(row => row.CUSIP_PPN).filter(Boolean);
        await addUserAction(userId, cusips, 'EXPORT_CLEARWATER');
        // Reload grid after successful user action save - export fields will always use server data
        await loadSecMaster(true, true); // Preserve local changes, silent reload
      } catch (error) {
        console.error('Error adding user actions:', error);
        notifyPermissionError(showNotification, error);
        // Don't fail the export if user action logging fails
      }

      // Restore column definitions immediately
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.setGridOption('columnDefs', originalColumnDefs);
      }

      showNotification('Clearwater Excel file exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting Clearwater data:', error);
      showNotification('Failed to export Clearwater data', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [transformDataForClearwater, applyClearwaterMapping, showNotification, userId, rowData, selectedForExport, loadSecMaster]);

  const handleExportMLx = useCallback(async () => {
    try {
      if (!gridRef.current || !gridRef.current.api) {
        showNotification('Grid not ready for export', 'error');
        return;
      }

      setIsExporting(true);
      setExportMenuAnchor(null);

      // Get only selected rows for export
      const selectedRowData = rowData.filter(row => selectedForExport.has(row.CUSIP_PPN));

      if (selectedRowData.length === 0) {
        showNotification('Please select at least one row to export', 'warning');
        setIsExporting(false);
        return;
      }

      // Transform data for MLx
      const transformedData = transformDataForMLx(selectedRowData);

      // Apply column mapping
      const mappedData = applyMLxMapping(transformedData);

      // Apply calculations and defaults
      const processedData = applyMLxCalculationsAndDefaults(mappedData);

      // Reorder columns according to MLx order
      const orderedData = reorderMLxColumns(processedData);

      // Store original column definitions
      const originalColumnDefs = gridRef.current.api.getColumnDefs();

      // Create new column definitions for MLx export
      const mlxColumnDefs = MLX_COLUMN_ORDER.map(col => ({
        field: col,
        headerName: col,
        width: 150
      }));

      // Temporarily update grid with ordered data and new column definitions
      gridRef.current.api.setGridOption('columnDefs', mlxColumnDefs);
      gridRef.current.api.setGridOption('rowData', orderedData);

      // Wait for grid to update before exporting
      setTimeout(() => {
        // Export with the new data
        const params = {
          fileName: `sec_master_mlx_${dayjs().format('YYYY-MM-DD')}.xlsx`,
          sheetName: 'Sec Master Entries',
          exportMode: 'xlsx',
          skipColumnHeaders: false,
          skipColumnGroupHeaders: false,
          skipPinnedTop: false,
          skipPinnedBottom: false,
          allColumns: true,
          onlySelected: false
        };

        gridRef.current.api.exportDataAsExcel(params);
      }, 100);

      // Add user actions for exported CUSIPs
      try {
        const cusips = selectedRowData.map(row => row.CUSIP_PPN).filter(Boolean);
        await addUserAction(userId, cusips, 'EXPORT_MLX');
        // Reload grid after successful user action save - export fields will always use server data
        await loadSecMaster(true, true); // Preserve local changes, silent reload
      } catch (error) {
        console.error('Error adding user actions:', error);
        notifyPermissionError(showNotification, error);
        // Don't fail the export if user action logging fails
      }

      // Restore column definitions immediately
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.setGridOption('columnDefs', originalColumnDefs);
      }

      showNotification('MLx Excel file exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting MLx data:', error);
      showNotification('Failed to export MLx data', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [transformDataForMLx, applyMLxMapping, applyMLxCalculationsAndDefaults, reorderMLxColumns, showNotification, userId, rowData, selectedForExport, loadSecMaster]);

  const handleExportMenuOpen = useCallback((event) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);

  const handleExportMenuClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  const handleAddSecurityMenuOpen = useCallback((event) => {
    if (writeBlockedRef.current) return false;
    setAddSecurityMenuAnchor(event.currentTarget);
  }, []);

  const handleAddSecurityMenuClose = useCallback(() => {
    setAddSecurityMenuAnchor(null);
  }, []);

  // Handle individual row selection for export
  const handleExportRowSelection = useCallback((rowId, isSelected) => {
    setSelectedForExport(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }
      return newSet;
    });
  }, []);

  // Handle select all/none for export
  const handleSelectAllForExport = useCallback((selectAll) => {
    if (selectAll) {
      const allRowIds = rowData.map(row => row.CUSIP_PPN);
      setSelectedForExport(new Set(allRowIds));
    } else {
      setSelectedForExport(new Set());
    }
  }, [rowData]);

  // Import SecMaster data from Excel file
  const importSecMaster = useCallback(async (file) => {
    if (writeBlockedRef.current) return false;
    try {
      setIsLoading(true);

      // Parse file using 1823-core shared utility (handles CSV, .xlsx, .xls)
      const rawRows = await parseFileData(file, { filterEmptyRows: true, preserveFalsy: true });

      if (!rawRows || rawRows.length === 0) {
        throw new Error('No data found in the uploaded file');
      }

      // Validate that all required Clearwater columns are present in the file headers
      const uploadedCols = new Set(Object.keys(rawRows[0]));
      const missingCols = [...REQUIRED_CW_COLS].filter(col => !uploadedCols.has(col));
      if (missingCols.length > 0) {
        throw new Error(`Upload must contain all Clearwater columns. Missing: ${missingCols.join(', ')}`);
      }

      // Normalize column headers: map clearwater names (and direct field names) to internal field names
      // using 1823-core normalizeRowKeys + the module-level cwColumnNormalizer
      const mappedData = rawRows.map(row => normalizeRowKeys(row, cwColumnNormalizer));

      // Filter to only include fields that exist in secMasterFields. CRE-only loan
      // fields are dropped: SEC_MASTER cannot store them, so an imported value would
      // show in the grid but never save.
      const filteredData = mappedData.map(row => pickImportFields(row, secMasterFields));

      // Validate field function - only validate format/types, not mandatory fields
      // Column presence is already validated above
      const validateField = (field, value) => {
        const fieldConfig = secMasterFields[field];
        if (!fieldConfig) return '';

        // Skip validation if value is empty/null
        if (value === null || value === undefined || value === '') {
          return '';
        }

        // Dropdown field validation - delegate to 1823-core choices validators
        if (choicesValidatorsByField[field]) {
          const error = choicesValidatorsByField[field]({ [field]: value });
          if (error) {
            return error.message;
          }
        }

        // Float validation - only validate format if value is provided
        const floatFields = ['TOTAL_COMMITMENT', 'STAT_CURRENT_UNITS', 'STAT_BOOK_VALUE', 'TRADE_PRICE'];
        if (floatFields.includes(field)) {
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue < 0) {
            return 'Value must be a number >= 0';
          }
        }

        // Percentage validation - accept both percentage format (5.5%) and decimal format (0.055)
        // Only validate format if value is provided
        const percentageFields = ['STAT_BOOK_YIELD', 'BASE_RATE', 'ALL_IN_RATE', 'FLOATER_SPREAD'];
        if (percentageFields.includes(field)) {
          const valueStr = value.toString().trim();
          if (valueStr.endsWith('%')) {
            const numValue = parseFloat(valueStr.slice(0, -1));
            if (isNaN(numValue) || numValue < 0 || numValue > 100) {
              return 'Percentage must be between 0% and 100%';
            }
          } else {
            const numValue = parseFloat(valueStr);
            if (isNaN(numValue) || numValue < 0 || numValue > 1) {
              return 'Decimal value must be between 0 and 1';
            }
          }
        }

        // Security Owner validation (should not end with '...') - only if value is provided
        if (field === 'SECURITY_OWNER' && value.endsWith('...')) {
          return 'Value must be present';
        }

        return '';
      };

      // Convert data types and validate
      const processedData = [];
      const validationErrors = [];

      filteredData.forEach((row, index) => {
        const processedRow = { ...row };
        const rowErrors = [];

        // Convert date fields
        const dateFields = ['TRADE_DATE', 'SETTLE_DATE', 'MATURITY_DATE', 'FIRST_COUPON_DATE', 'RATING_DATE', 'ACCRUED_INTEREST_START_DATE'];
        dateFields.forEach(field => {
          if (processedRow[field]) {
            try {
              const date = new Date(processedRow[field]);
              if (!isNaN(date.getTime())) {
                processedRow[field] = date.toISOString().split('T')[0];
              } else {
                processedRow[field] = null;
              }
            } catch (e) {
              console.warn(`Invalid date format for ${field} in row ${index + 1}:`, processedRow[field]);
              processedRow[field] = null;
            }
          } else {
            processedRow[field] = null;
          }
        });

        // Convert boolean fields
        const booleanFields = ['FLOATER', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING', 'RATING_ESTIMATE'];
        booleanFields.forEach(field => {
          if (processedRow[field] === undefined || processedRow[field] === null) {
            processedRow[field] = false;
          } else {
            // Convert string 'Y'/'N' or boolean to boolean
            processedRow[field] = processedRow[field] === true || processedRow[field] === 'Y' || processedRow[field] === 'y';
          }
        });

        // Convert percentage fields to decimal format (same as modal)
        const percentageFields = ['STAT_BOOK_YIELD', 'BASE_RATE', 'ALL_IN_RATE', 'FLOATER_SPREAD'];
        percentageFields.forEach(field => {
          if (processedRow[field] !== null && processedRow[field] !== undefined && processedRow[field] !== '') {
            const valueStr = processedRow[field].toString().trim();
            if (valueStr.endsWith('%')) {
              const numValue = parseFloat(valueStr.slice(0, -1));
              if (!isNaN(numValue)) {
                processedRow[field] = numValue / 100;
              } else {
                processedRow[field] = null;
              }
            } else {
              const numValue = parseFloat(valueStr);
              if (!isNaN(numValue)) {
                processedRow[field] = numValue;
              } else {
                processedRow[field] = null;
              }
            }
          } else {
            processedRow[field] = null;
          }
        });

        // Convert numeric fields
        const numericFields = ['TOTAL_COMMITMENT', 'STAT_CURRENT_UNITS', 'STAT_BOOK_VALUE', 'TRADE_PRICE'];
        numericFields.forEach(field => {
          if (processedRow[field] !== undefined && processedRow[field] !== null && processedRow[field] !== '') {
            const numValue = parseFloat(processedRow[field]);
            if (!isNaN(numValue)) {
              processedRow[field] = numValue;
            } else {
              processedRow[field] = null;
            }
          } else {
            processedRow[field] = null;
          }
        });

        // Validate field formats/types (not mandatory fields - columns are already validated)
        Object.keys(processedRow).forEach(field => {
          const error = validateField(field, processedRow[field]);
          if (error) {
            rowErrors.push(`${field}: ${error}`);
          }
        });

        if (rowErrors.length > 0) {
          validationErrors.push(`Row ${index + 1} (CUSIP_PPN: ${processedRow.CUSIP_PPN || 'N/A'}): ${rowErrors.join('; ')}`);
        } else {
          // Add metadata fields (the writer stamps MODIFIED_BY / MODIFIED_DATE)
          processedRow.CREATED_BY = userId;
          processedRow.CREATED_DATE = new Date().toISOString();

          // If RATING is present, automatically set NAIC_DESIGNATION
          if (processedRow.RATING) {
            const naicDesignation = getNaicDesignationFromRating(processedRow.RATING);
            if (naicDesignation) {
              processedRow.NAIC_DESIGNATION = naicDesignation;
            }
          }

          // If ASSET_TYPE is present, automatically set COLLATERAL_TYPE
          if (processedRow.ASSET_TYPE) {
            const collateralType = getCollateralTypeFromAssetType(processedRow.ASSET_TYPE);
            if (collateralType) {
              processedRow.COLLATERAL_TYPE = collateralType;
            }
          }

          processedData.push(processedRow);
        }
      });

      // Show validation errors if any
      if (validationErrors.length > 0) {
        const errorMessage = `There were validation errors with the security upload:\n\n${validationErrors.join('\n')}`;
        showNotification(errorMessage, 'error');
        return;
      }

      // A row uploaded without a CUSIP/PPN gets its synthetic id now, so the merge
      // and the `created` tracking below both use the id the row actually carries.
      processedData.forEach(row => {
        if (isValueNull(row.CUSIP_PPN)) {
          row.CUSIP_PPN = `new_row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
      });

      // Process imports using 1823-core mergeRows: upsert by CUSIP_PPN, track changes for highlighting
      const currentTime = new Date().toISOString();
      const actualChangesMap = new Map();
      // Uploaded keys that matched a loaded row; unchanged matches are not new rows
      const matchedKeys = new Set();

      setRowData(prevData => {
        const { mergedData, newCount, updatedCount, updatedKeys } = mergeRows({
          uploadedRows: processedData,
          existingRows: prevData,
          matchField: 'CUSIP_PPN',
          mergeStrategy: 'upsert',
          onNewRow: (row) => ({ ...row }),
          onUpdateRow: (existingRow, importedRow) => {
            matchedKeys.add(existingRow.CUSIP_PPN);
            // Custom update: compare fields (excluding metadata), track changes,
            // handle RATING→NAIC_DESIGNATION and ASSET_TYPE→COLLATERAL_TYPE cascades
            const actualChanges = {};
            let hasChanges = false;
            const updatedRow = { ...existingRow };

            Object.keys(importedRow).forEach(field => {
              if (field === 'MODIFIED_BY' || field === 'MODIFIED_DATE' || field === 'CREATED_BY' || field === 'CREATED_DATE') {
                return; // Skip metadata fields in comparison
              }

              const existingValue = existingRow[field];
              const newValue = importedRow[field];

              const existingValueStr = existingValue != null ? String(existingValue).trim() : '';
              const newValueStr = newValue != null ? String(newValue).trim() : '';

              if (existingValueStr !== newValueStr) {
                actualChanges[field] = { oldValue: existingValue, newValue, timestamp: currentTime };
                hasChanges = true;
                updatedRow[field] = newValue;

                // If RATING changed, automatically update NAIC_DESIGNATION
                if (field === 'RATING' && newValue) {
                  const naicDesignation = getNaicDesignationFromRating(newValue);
                  if (naicDesignation) {
                    actualChanges.NAIC_DESIGNATION = {
                      oldValue: existingRow.NAIC_DESIGNATION,
                      newValue: naicDesignation,
                      timestamp: currentTime
                    };
                    updatedRow.NAIC_DESIGNATION = naicDesignation;
                  }
                }

                // If ASSET_TYPE changed, automatically update COLLATERAL_TYPE
                if (field === 'ASSET_TYPE' && newValue) {
                  const collateralType = getCollateralTypeFromAssetType(newValue);
                  if (collateralType) {
                    actualChanges.COLLATERAL_TYPE = {
                      oldValue: existingRow.COLLATERAL_TYPE,
                      newValue: collateralType,
                      timestamp: currentTime
                    };
                    updatedRow.COLLATERAL_TYPE = collateralType;
                  }
                }
              }
            });

            if (hasChanges) {
              actualChangesMap.set(existingRow.CUSIP_PPN, actualChanges);
            }

            return updatedRow;
          },
        });

        // Expose counts to the outer scope via closure refs
        mergeRows._lastResult = { newCount, updatedCount, updatedKeys };
        return mergedData;
      });

      // Because setRowData is async we read the counts from the closure captured during the merge
      // We approximate by using the processedData and prevData lengths after the fact.
      // The actual counts are captured in actualChangesMap and newRows during the merge above.
      const updatedCusips = new Set(actualChangesMap.keys());
      // New rows are those in processedData whose CUSIP_PPN wasn't in prevData —
      // we detect them as processedData entries not in actualChangesMap and not in existing data.
      // Since we can't read prevData here again, we rely on notifying based on processedData total.
      const totalUpdated = updatedCusips.size;
      const totalNew = processedData.filter(row => !matchedKeys.has(row.CUSIP_PPN)).length;

      // Track changes in updatedRows for highlighting
      setUpdatedRows(prev => {
        const newUpdatedRows = { ...prev };

        // Track updated rows
        actualChangesMap.forEach((actualChanges, cusipPpn) => {
          newUpdatedRows[cusipPpn] = {
            ...(newUpdatedRows[cusipPpn] || {}),
            ...actualChanges
          };
        });

        // Track new rows (processedData entries that matched no loaded row). The save
        // route writes every row sent, so an unchanged match must not be re-sent.
        processedData.forEach(row => {
          const cusipPpn = row.CUSIP_PPN;
          if (!matchedKeys.has(cusipPpn)) {
            newUpdatedRows[cusipPpn] = {
              created: {
                oldValue: null,
                newValue: 'new_row',
                timestamp: currentTime
              }
            };
          }
        });

        return newUpdatedRows;
      });

      // Refresh grid to apply highlighting
      if (gridRef.current && gridRef.current.api && processedData.length > 0) {
        gridRef.current.api.redrawRows();
      }

      // Show success message with statistics
      const totalRowsProcessed = totalUpdated + totalNew;
      if (totalRowsProcessed === 0) {
        showNotification('No changes detected in uploaded file', 'warning');
      } else {
        let message = `Successfully imported ${totalRowsProcessed} row${totalRowsProcessed === 1 ? '' : 's'}`;
        if (totalUpdated > 0 && totalNew > 0) {
          message += ` (${totalUpdated} updated, ${totalNew} new)`;
        } else if (totalUpdated > 0) {
          message += ` (${totalUpdated} updated)`;
        } else if (totalNew > 0) {
          message += ` (${totalNew} new)`;
        }
        message += '. Click Save to persist changes.';
        showNotification(message, 'success');
      }

    } catch (error) {
      console.error('Error importing data:', error);
      showNotification(`Failed to import data: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [userId, showNotification]);

  // Handle file input change for import
  const handleImportFileChange = useCallback((event) => {
    if (writeBlockedRef.current) return false;
    const file = event.target.files[0];
    if (file) {
      importSecMaster(file);
      // Reset the input so the same file can be selected again
      event.target.value = '';
    }
  }, [importSecMaster]);


  // Function to enhance column definitions with secMasterFields data
  const enhanceColumnDef = useCallback((columnDef) => {
    const fieldKey = columnDef.field;
    const fieldConfig = secMasterFields[fieldKey];

    if (!fieldConfig) {
      return columnDef;
    }

    const enhanced = { ...columnDef };

    // Add tooltip from field config if not already present
    if (fieldConfig.name && !enhanced.headerTooltip) {
      enhanced.headerTooltip = fieldConfig.name;
    }

    // Add dropdown editor if choices are available and not already set
    if (fieldConfig.choices && Array.isArray(fieldConfig.choices) && !enhanced.cellEditor) {
      enhanced.cellEditor = 'agSelectCellEditor';
      enhanced.cellEditorParams = {
        values: fieldConfig.choices
      };
    }

    return enhanced;
  }, []);

  // Memoize cell renderers to prevent recreation on every render
  const exportCellRenderer = useCallback((params) => {
    const { data } = params;
    return React.createElement(Checkbox, {
      checked: selectedForExport.has(data.CUSIP_PPN),
      onChange: (e) => handleExportRowSelection(data.CUSIP_PPN, e.target.checked),
      color: "primary",
      size: "small"
    });
  }, [selectedForExport, handleExportRowSelection]);

  const headerCheckboxRenderer = useCallback(() => {
    return React.createElement(Box, {
      sx: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }
    }, React.createElement(Checkbox, {
      checked: selectedForExport.size > 0 && selectedForExport.size === rowData.length,
      indeterminate: selectedForExport.size > 0 && selectedForExport.size < rowData.length,
      onChange: (e) => handleSelectAllForExport(e.target.checked),
      color: "primary",
      size: "small"
    }));
  }, [selectedForExport, rowData.length, handleSelectAllForExport]);

  const actionsCellRenderer = useCallback((params) => {
    const isCRE = params.data && params.data.SECURITY_OWNER === 'CRE';
    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        <Tooltip title="View History">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleViewHistory(params.data)}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isCRE ? "Cannot edit CRE entries" : "Edit Entry"}>
          <span>
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleEditSecMaster(params.data)}
              disabled={isCRE || editingBlocked || isLoading}
            >
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isCRE ? "Cannot duplicate CRE entries" : "Duplicate Entry"}>
          <span>
            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleDuplicateSecMaster(params.data)}
              disabled={isCRE || editingBlocked || isLoading}
            >
              <DuplicateIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isCRE ? "Cannot delete CRE entries" : "Delete Entry"}>
          <span>
            <IconButton
              color="error"
              size="small"
              onClick={() => handleDeleteSecMaster(params.data)}
              disabled={isCRE || editingBlocked || isLoading}
            >
              <DeleteIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }, [handleViewHistory, handleEditSecMaster, handleDuplicateSecMaster, handleDeleteSecMaster, editingBlocked, isLoading]);

  // Helper function to reorder columns according to fieldOrder
  const reorderColumns = useCallback((columns) => {
    // Field order from SecurityModal to match modal order
    const fieldOrder = [
      'SECURITY_OWNER', 'SECURITY_OWNER_TEAM', 'SECURITY_DESCRIPTION', 'CUSIP_PPN',
      'TRADE_DATE', 'ISSUER', 'TICKER', 'SECURITY_TYPE', 'SETTLE_DATE', 'SETTLEMENT_TYPE',
      'TOTAL_COMMITMENT', 'STAT_CURRENT_UNITS', 'STAT_BOOK_VALUE', 'STAT_BOOK_YIELD',
      'TRADE_PRICE', 'STAT_CLASSIFICATION', 'NAIC_GENERAL_CATEGORY', 'AFFILIATED_UNAFFILIATED',
      'ASSET_TYPE', 'COLLATERAL_TYPE', 'BROKER_DEALER', 'CLOSING_CONTACT', 'CONNECTED_ENTITY',
      'COUNTRY_OF_ISSUE', 'CURRENCY', 'PIK_INTEREST', 'COUPON_FREQUENCY', 'COUPON_TYPE',
      'FIRST_COUPON_DATE', 'FLOATER', 'BASE_RATE', 'FLOATER_SPREAD', 'ALL_IN_RATE',
      'DAY_COUNT', 'MATURITY_DATE', 'BALLOON_PAYMENT', 'ILLIQUID', 'PRIVATE_RATING',
      'RATING_ESTIMATE', 'RATING', 'NAIC_DESIGNATION', 'RATING_AGENCY', 'RATING_DATE',
      'REFERENCE_INDEX', 'COMMENTS', 'STATUS', 'AMORTIZATION_TYPE', 'ACCRUED_INTEREST_START_DATE',
      'CW_ACCOUNT_NAME', 'CW_ACCOUNT_NUMBER', 'CUSTODIAN_NAME', 'CUSTODIAN_ACCOUNT_NUMBER',
      'BORROWER', 'SERVICE_FEE', 'RECOURSE_TYPE', 'ORIGINAL_LOAN_TERM', 'INTEREST_ONLY_TERM',
      'LAST_EXPORT_USER', 'LAST_EXPORT_DATE'
    ];

    // Separate special columns (Export, Actions) and data columns
    const specialColumns = columns.filter(col => !col.field || col.field === 'export' || col.field === 'actions');
    const dataColumns = columns.filter(col => col.field && col.field !== 'export' && col.field !== 'actions');

    // Create a map of columns by field name
    const columnMap = {};
    dataColumns.forEach(col => {
      if (col.field) {
        columnMap[col.field] = col;
      }
    });

    // Build ordered columns array
    const orderedDataColumns = [];

    // Add columns in fieldOrder
    fieldOrder.forEach(field => {
      if (columnMap[field]) {
        orderedDataColumns.push(columnMap[field]);
        delete columnMap[field]; // Remove from map so we don't add it twice
      }
    });

    // Add any remaining columns not in fieldOrder (MODIFIED_BY, MODIFIED_DATE)
    Object.values(columnMap).forEach(col => {
      orderedDataColumns.push(col);
    });

    // Combine: special columns (pinned) + ordered data columns
    return [...specialColumns, ...orderedDataColumns];
  }, []);

  const columnDefs = useMemo(() => {
    const baseColumns = [
      {
        headerName: "Export",
        headerTooltip: "Select rows to include in export",
        field: "export",
        cellStyle: { textAlign: "center" },
        maxWidth: 80,
        pinned: 'left',
        cellRenderer: exportCellRenderer,
        headerComponent: headerCheckboxRenderer
      },
      {
        headerName: "Actions",
        headerTooltip: "Available actions for this entry: view history, edit, duplicate, or delete",
        field: "actions",
        cellStyle: { textAlign: "center" },
        maxWidth: 200,
        pinned: 'left',
        cellRenderer: actionsCellRenderer,
        sortable: false,
        filter: false,
        getQuickFilterText: ''
      },
      {
        headerName: "CUSIP/PPN",
        headerTooltip: "Committee on Uniform Securities Identification Procedures number or Private Placement Number",
        editable: (params) => {
          if (writeBlockedRef.current) return false;
          // Do not allow editing if SECURITY_OWNER is CRE
          if (params.data && params.data.SECURITY_OWNER === 'CRE') return false;
          // Only allow editing if CUSIP_PPN starts with 'new_row_'
          return params.data && params.data.CUSIP_PPN && params.data.CUSIP_PPN.startsWith('new_row_');
        },
        field: "CUSIP_PPN",
        pinned: 'left',
        width: 150
      },
      {
        headerName: "Trade Date",
        headerTooltip: "Date when the security was traded",
        field: "TRADE_DATE",
        width: 120,
        cellEditor: 'agDateCellEditor',
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value);
        }
      },
      {
        headerName: "Security Owner",
        headerTooltip: "Entity that owns the security",
        field: "SECURITY_OWNER",
        width: 120
      },
      {
        headerName: "Security Description",
        headerTooltip: "Full description of the security",
        field: "SECURITY_DESCRIPTION",
        width: 200
      },
      {
        headerName: "Base Rate",
        headerTooltip: "Base interest rate",
        field: "BASE_RATE",
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          max: 1,
          precision: 4
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseFloat(params.value);
          if (isNaN(numValue)) return '';
          return (numValue * 100).toFixed(4) + '%';
        }
      },
      {
        headerName: "Ticker",
        headerTooltip: "Stock ticker symbol",
        field: "TICKER",
        width: 100
      },
      {
        headerName: "Issuer",
        headerTooltip: "The entity that issued the security",
        field: "ISSUER",
        width: 150
      },
      {
        headerName: "Borrower",
        headerTooltip: "The borrower of the loan",
        field: "BORROWER",
        // CRE-only: filled from CRE_DB for CRE rows; SEC_MASTER cannot store it.
        editable: false,
        width: 150
      },
      {
        headerName: "Asset Type",
        headerTooltip: "Type of underlying asset",
        field: "ASSET_TYPE",
        width: 120
      },
      {
        headerName: "Security Type",
        headerTooltip: "The type/category of security",
        field: "SECURITY_TYPE",
        width: 120
      },
      {
        headerName: "Broker/Dealer",
        headerTooltip: "Broker or dealer information",
        field: "BROKER_DEALER",
        width: 150
      },
      {
        headerName: "Closing Contact",
        headerTooltip: "Contact person for closing",
        field: "CLOSING_CONTACT",
        width: 150
      },
      {
        headerName: "Day Count",
        headerTooltip: "Day count convention",
        field: "DAY_COUNT",
        width: 120
      },
      {
        headerName: "PIK Interest",
        headerTooltip: "Paid-in-kind interest (Y/N)",
        field: "PIK_INTEREST",
        width: 120
      },
      {
        headerName: "Security Owner Team",
        headerTooltip: "Team responsible for the security",
        field: "SECURITY_OWNER_TEAM",
        width: 150
      },
      {
        headerName: "Settlement Type",
        headerTooltip: "Type of settlement",
        field: "SETTLEMENT_TYPE",
        width: 150
      },
      {
        headerName: "Currency",
        headerTooltip: "The currency denomination for this security",
        field: "CURRENCY",
        width: 80,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY']
        }
      },
      {
        headerName: "Status",
        headerTooltip: "Current status of the security",
        field: "STATUS",
        width: 100,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Active', 'Draft', 'Deleted']
        }
      },
      {
        headerName: "Settle Date",
        headerTooltip: "Date when the trade settles",
        field: "SETTLE_DATE",
        width: 120,
        cellEditor: 'agDateCellEditor',
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value);
        }
      },
      {
        headerName: "Maturity Date",
        headerTooltip: "The date when the security matures",
        field: "MATURITY_DATE",
        width: 120,
        cellEditor: 'agDateCellEditor',
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value);
        }
      },
      {
        headerName: "Trade Price",
        headerTooltip: "Price at which the security was traded",
        field: "TRADE_PRICE",
        width: 120,
        editable: (params) => {
          if (isLoading || writeBlockedRef.current) return false;
          if (params.data && params.data.SECURITY_OWNER === 'CRE') return false;
          return true;
        },
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 4
        },
        valueParser: (params) => {
          if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
            return null;
          }
          const value = typeof params.newValue === 'string'
            ? parseFloat(params.newValue.replace(/[^0-9.-]/g, ''))
            : parseFloat(params.newValue);
          return isNaN(value) ? null : value;
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 4
          }).format(params.value);
        }
      },
      {
        headerName: "Book Value",
        headerTooltip: "Current book value of the security",
        field: "STAT_BOOK_VALUE",
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 2
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(params.value);
        }
      },
      {
        headerName: "Current Units",
        headerTooltip: "Current number of units held",
        field: "STAT_CURRENT_UNITS",
        width: 120,
        editable: (params) => {
          if (isLoading || writeBlockedRef.current) return false;
          if (params.data && params.data.SECURITY_OWNER === 'CRE') return false;
          return true;
        },
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 0
        },
        valueParser: (params) => {
          if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
            return null;
          }
          const value = typeof params.newValue === 'string'
            ? parseInt(params.newValue.replace(/[^0-9-]/g, ''), 10)
            : parseInt(params.newValue, 10);
          return isNaN(value) ? null : value;
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseInt(params.value, 10);
          if (isNaN(numValue)) return '';
          return numValue.toString();
        }
      },
      {
        headerName: "Total Commitment",
        headerTooltip: "Total commitment amount",
        field: "TOTAL_COMMITMENT",
        width: 120,
        editable: (params) => {
          if (isLoading || writeBlockedRef.current) return false;
          if (params.data && params.data.SECURITY_OWNER === 'CRE') return false;
          return true;
        },
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 2
        },
        valueParser: (params) => {
          if (params.newValue === null || params.newValue === undefined || params.newValue === '') {
            return null;
          }
          const value = typeof params.newValue === 'string'
            ? parseFloat(params.newValue.replace(/[^0-9.-]/g, ''))
            : parseFloat(params.newValue);
          return isNaN(value) ? null : value;
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(params.value);
        }
      },
      {
        headerName: "Coupon Type",
        headerTooltip: "Type of coupon payment",
        field: "COUPON_TYPE",
        width: 120,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Fixed', 'Floating', 'Variable', 'Zero']
        }
      },
      {
        headerName: "Coupon Frequency",
        headerTooltip: "Frequency of coupon payments",
        field: "COUPON_FREQUENCY",
        width: 120,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual', 'Zero']
        }
      },
      {
        headerName: "All-In Rate",
        headerTooltip: "All-in interest rate",
        field: "ALL_IN_RATE",
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          max: 1,
          precision: 4
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseFloat(params.value);
          if (isNaN(numValue)) return '';
          return (numValue * 100).toFixed(4) + '%';
        }
      },
      {
        headerName: "Book Yield",
        headerTooltip: "Current book yield",
        field: "STAT_BOOK_YIELD",
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          max: 1,
          precision: 4
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseFloat(params.value);
          if (isNaN(numValue)) return '';
          return (numValue * 100).toFixed(4) + '%';
        }
      },
      {
        headerName: "Amortization Type",
        headerTooltip: "Type of amortization",
        field: "AMORTIZATION_TYPE",
        width: 150,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Amortizing', 'Amortizing with Balloon', 'Interest Only', 'Bullet']
        }
      },
      {
        headerName: "Recourse Type",
        headerTooltip: "Type of recourse",
        field: "RECOURSE_TYPE",
        // CRE-only: filled from CRE_DB for CRE rows; SEC_MASTER cannot store it.
        editable: false,
        width: 120,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Full Recourse', 'Ltd Recourse', 'Non-Recourse']
        }
      },
      {
        headerName: "Country of Issue",
        headerTooltip: "Country where the security was issued",
        field: "COUNTRY_OF_ISSUE",
        width: 120
      },
      {
        headerName: "Rating",
        headerTooltip: "Credit rating of the security",
        field: "RATING",
        width: 100
      },
      {
        headerName: "NAIC Designation",
        headerTooltip: "NAIC designation",
        field: "NAIC_DESIGNATION",
        width: 150,
        editable: false
      },
      {
        headerName: "Rating Agency",
        headerTooltip: "Agency that provided the rating",
        field: "RATING_AGENCY",
        width: 120
      },
      {
        headerName: "Service Fee",
        headerTooltip: "Service fee percentage",
        field: "SERVICE_FEE",
        // CRE-only: filled from CRE_DB for CRE rows; SEC_MASTER cannot store it.
        editable: false,
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          max: 1,
          precision: 4
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseFloat(params.value);
          if (isNaN(numValue)) return '';
          return (numValue * 100).toFixed(4) + '%';
        }
      },
      {
        headerName: "Interest Only Term",
        headerTooltip: "Interest only period in months",
        field: "INTEREST_ONLY_TERM",
        // CRE-only: filled from CRE_DB for CRE rows; SEC_MASTER cannot store it.
        editable: false,
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 0
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseInt(params.value, 10);
          if (isNaN(numValue)) return '';
          return numValue.toString();
        }
      },
      {
        headerName: "Original Loan Term",
        headerTooltip: "Original loan term in months",
        field: "ORIGINAL_LOAN_TERM",
        // CRE-only: filled from CRE_DB for CRE rows; SEC_MASTER cannot store it.
        editable: false,
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 0
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseInt(params.value, 10);
          if (isNaN(numValue)) return '';
          return numValue.toString();
        }
      },
      {
        headerName: "STAT Classification",
        headerTooltip: "STAT classification",
        field: "STAT_CLASSIFICATION",
        width: 150
      },
      {
        headerName: "NAIC General Category",
        headerTooltip: "NAIC general category",
        field: "NAIC_GENERAL_CATEGORY",
        width: 180
      },
      {
        headerName: "Affiliated/Unaffiliated",
        headerTooltip: "Whether the security is affiliated or unaffiliated",
        field: "AFFILIATED_UNAFFILIATED",
        width: 150,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Affiliated', 'Unaffiliated']
        }
      },
      {
        headerName: "Collateral Type",
        headerTooltip: "Type of collateral",
        field: "COLLATERAL_TYPE",
        width: 150
      },
      {
        headerName: "Connected Entity",
        headerTooltip: "Connected entity information",
        field: "CONNECTED_ENTITY",
        width: 150
      },
      {
        headerName: "First Coupon Date",
        headerTooltip: "Date of the first coupon payment",
        field: "FIRST_COUPON_DATE",
        width: 150,
        cellEditor: 'agDateCellEditor',
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value);
        }
      },
      {
        headerName: "Floater",
        headerTooltip: "Whether the security is a floater (Y/N)",
        field: "FLOATER",
        width: 100,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Y', 'N', '']
        }
      },
      {
        headerName: "Floater Spread",
        headerTooltip: "Floater spread",
        field: "FLOATER_SPREAD",
        width: 120,
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          max: 1,
          precision: 4
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const numValue = typeof params.value === 'number' ? params.value : parseFloat(params.value);
          if (isNaN(numValue)) return '';
          return (numValue * 100).toFixed(4) + '%';
        }
      },
      {
        headerName: "Balloon Payment",
        headerTooltip: "Whether there is a balloon payment (Y/N)",
        field: "BALLOON_PAYMENT",
        width: 130,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Y', 'N', '']
        }
      },
      {
        headerName: "Illiquid",
        headerTooltip: "Whether the security is illiquid (Y/N)",
        field: "ILLIQUID",
        width: 100,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Y', 'N', '']
        }
      },
      {
        headerName: "Private Rating",
        headerTooltip: "Whether the rating is private (Y/N)",
        field: "PRIVATE_RATING",
        width: 120,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Y', 'N', '']
        }
      },
      {
        headerName: "Rating Estimate",
        headerTooltip: "Whether the rating is an estimate (Y/N)",
        field: "RATING_ESTIMATE",
        width: 130,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['Y', 'N', '']
        }
      },
      {
        headerName: "Rating Date",
        headerTooltip: "Date when the rating was assigned",
        field: "RATING_DATE",
        width: 120,
        cellEditor: 'agDateCellEditor',
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value);
        }
      },
      {
        headerName: "Reference Index",
        headerTooltip: "Reference index for floating rate securities",
        field: "REFERENCE_INDEX",
        width: 180,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: {
          values: ['SOFR 1 MONTH TERM RATE', 'SOFR 3 MONTH TERM RATE', 'SOFR 6 MONTH TERM RATE']
        }
      },
      {
        headerName: "Accrued Interest Start Date",
        headerTooltip: "Date when accrued interest starts",
        field: "ACCRUED_INTEREST_START_DATE",
        width: 180,
        cellEditor: 'agDateCellEditor',
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDate(params.value);
        }
      },
      {
        headerName: "CW Account Name",
        headerTooltip: "Clearwater account name",
        field: "CW_ACCOUNT_NAME",
        width: 150
      },
      {
        headerName: "CW Account #",
        headerTooltip: "Clearwater account number",
        field: "CW_ACCOUNT_NUMBER",
        width: 120
      },
      {
        headerName: "Custodian Name",
        headerTooltip: "Custodian name",
        field: "CUSTODIAN_NAME",
        width: 150
      },
      {
        headerName: "Custodian Account #",
        headerTooltip: "Custodian account number",
        field: "CUSTODIAN_ACCOUNT_NUMBER",
        width: 150
      },
      {
        headerName: "Last Export User",
        headerTooltip: "User who last exported this entry",
        field: "LAST_EXPORT_USER",
        editable: false,
        width: 150
      },
      {
        headerName: "Last Export Date",
        headerTooltip: "Date when this entry was last exported",
        field: "LAST_EXPORT_DATE",
        editable: false,
        width: 180,
        // Real TIMESTAMP_NTZ (USER_ACTIONS.TIMESTAMP) with a time-of-day — show
        // date AND time in local zone. formatDate (UTC date-only) would shift an
        // evening Eastern timestamp to the next day now that the backend sends UTC.
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDateTimeForDisplay(params.value);
        }
      },
      {
        headerName: "Comments",
        headerTooltip: "Additional comments or notes",
        field: "COMMENTS",
        width: 200
      },
      {
        headerName: "Modified By",
        headerTooltip: "User who last modified this entry",
        field: "MODIFIED_BY",
        editable: false,
        width: 200
      },
      {
        headerName: "Modified Date",
        headerTooltip: "Date and time when this entry was last modified",
        field: "MODIFIED_DATE",
        editable: false,
        width: 180,
        // Real TIMESTAMP_NTZ with a time-of-day; show date AND time in local zone
        // (matches the tooltip). formatDate would day-shift evening timestamps.
        valueFormatter: (params) => {
          if (!params.value) return '';
          return formatDateTimeForDisplay(params.value);
        }
      }
    ];

    // Reorder columns to match SecurityModal field order
    const reorderedColumns = reorderColumns(baseColumns);

    // Enhance each column with secMasterFields data
    const enhancedColumns = reorderedColumns.map(enhanceColumnDef);

    // Verify all required fields from config.json are present in column definitions
    const requiredFields = config.validation.requiredFields;
    const columnFields = enhancedColumns.map(col => col.field).filter(Boolean);
    const missingFields = requiredFields.filter(field => !columnFields.includes(field));

    if (missingFields.length > 0) {
      console.error('Missing required fields in column definitions:', missingFields);
      console.warn('Required fields:', requiredFields);
      console.warn('Column fields:', columnFields);
    }

    return enhancedColumns;
  }, [enhanceColumnDef, exportCellRenderer, headerCheckboxRenderer, actionsCellRenderer, reorderColumns, isLoading]);

  // Create a mapping from field names to header names for better validation error display
  const fieldToHeaderMap = useMemo(() => {
    const mapping = {};
    columnDefs.forEach(colDef => {
      if (colDef.field && colDef.headerName) {
        mapping[colDef.field] = colDef.headerName;
      }
    });
    return mapping;
  }, [columnDefs]);

  // Function to determine cell classes - highlight entire row, with changed cells standing out more
  const getCellClass = useCallback((params) => {
    const { data, colDef } = params;
    if (!data || !colDef || !colDef.field) return '';

    // Skip action columns
    if (['actions', 'export'].includes(colDef.field)) {
      return '';
    }

    const cusipPpn = data.CUSIP_PPN;
    if (!cusipPpn) return '';

    const field = colDef.field;
    const rowUpdates = updatedRows[cusipPpn];
    const hasRowUpdates = rowUpdates && Object.keys(rowUpdates).length > 0;
    const isNewRow = cusipPpn.startsWith('new_row_') || (rowUpdates && rowUpdates.created);
    const isChangedCell = rowUpdates && rowUpdates[field] && field !== 'created';

    if (isNewRow) {
      // For new rows, all cells get the new row highlight
      return 'ag-cell-updated-new-row';
    } else if (isChangedCell) {
      // Specific changed cell gets more prominent highlighting
      return 'ag-cell-changed';
    } else if (hasRowUpdates) {
      // Other cells in the row get lighter highlighting
      return 'ag-cell-updated';
    }

    return '';
  }, [updatedRows]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    editable: (params) => {
      // Do not allow editing if loading or if SECURITY_OWNER is CRE
      if (isLoading || writeBlockedRef.current) return false;
      if (params.data && params.data.SECURITY_OWNER === 'CRE') return false;
      return true;
    },
    cellClass: getCellClass,
    menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
    lockPinned: false
  }), [isLoading, getCellClass]);

  const gridOptions = useMemo(() => ({
    // Performance optimizations
    suppressColumnVirtualisation: false,
    suppressRowVirtualisation: false,
    rowBuffer: 10,
    suppressAnimationFrame: false,
    suppressBrowserResizeObserver: false,

    // Auto-sizing strategy
    autoSizeStrategy: {
      type: 'fitCellContents',
      defaultMinWidth: 100,
      columnLimits: [
        { colId: 'actions', maxWidth: 200 },
        { colId: 'export', maxWidth: 80 }
      ]
    },

    // Enable cell selection highlighting
    enableRangeSelection: true,
    enableCellTextSelection: true,
    suppressRowClickSelection: true,

    // Enable copy/paste functionality for cell ranges (defaults apply in v33)
    copyHeadersToClipboard: false,
    copyGroupHeadersToClipboard: false,
    suppressLastEmptyLineOnPaste: true,
    clipboardDelimiter: '\t',
    suppressClipboardPaste: false,

    // Performance: Disable expensive features
    suppressRowHoverHighlight: true,
    suppressColumnMoveAnimation: true,
    suppressNoRowsOverlay: false
  }), []);


  const handleEditFilter = useCallback((e) => {
    setQuickFilterText(e.target.value);
  }, []);

  const handleDateChange = useCallback((newDate) => {
    setSelectedDate(newDate);
    // Data will be reloaded automatically due to the dependency in loadSecMaster
  }, []);


  const onCellValueChanged = useCallback((params) => {
    if (writeBlockedRef.current) return false;
    // Handle cell value changes during paste operations
    // Do not allow editing if SECURITY_OWNER is CRE
    if (params.data && params.data.SECURITY_OWNER === 'CRE') {
      console.warn('Cannot edit rows with SECURITY_OWNER = CRE');
      return;
    }
    // CRE-only loan fields are display-only (SEC_MASTER has no such columns):
    // put back any value that reached the row and record no change.
    if (params.colDef && isCreOnlyField(params.colDef.field)) {
      if (params.data) params.data[params.colDef.field] = params.oldValue;
      params.api?.refreshCells?.({ rowNodes: params.node ? [params.node] : undefined, force: true });
      return;
    }

    // Update the row data to reflect the change and set modification tracking
    const { data, colDef, newValue } = params;
    // Typing the real CUSIP/PPN into a new row re-keys it. AG Grid has already
    // written the new id into `data`, so the row and its tracked changes (including
    // `created`, which keeps tokenFor at null) are still filed under the old id.
    // Without moving them, a CUSIP that matches a loaded security would be sent
    // with that security's MODIFIED_DATE and silently overwrite it.
    const renamedFrom = colDef.field === 'CUSIP_PPN' && isSyntheticCusipPpn(params.oldValue)
      && params.oldValue !== newValue ? params.oldValue : null;
    if (renamedFrom) {
      // One tracking entry per CUSIP/PPN cannot hold two rows: refuse a blank id or
      // one another loaded or local row already uses, and restore the placeholder.
      const blank = isValueNull(typeof newValue === 'string' ? newValue.trim() : newValue);
      const clash = blank || isSyntheticCusipPpn(newValue)
        || loadedTokens.current.has(newValue)
        || rowDataRef.current.some(row => row !== data && row.CUSIP_PPN === newValue);
      if (clash) {
        data.CUSIP_PPN = renamedFrom;
        params.api?.refreshCells?.({ rowNodes: params.node ? [params.node] : undefined, force: true });
        showNotification(
          blank
            ? 'Enter a CUSIP/PPN for the new row.'
            : `CUSIP/PPN ${newValue} is already in the grid; enter a different one.`,
          'error'
        );
        return;
      }
    }
    const cusipPpn = renamedFrom ? newValue : data.CUSIP_PPN;
    const lookupKey = renamedFrom || cusipPpn;
    const currentTime = new Date().toISOString();
    // MODIFIED_BY / MODIFIED_DATE are never restamped locally: MODIFIED_DATE is the
    // loaded token the save sends back, and the writer stamps both on save.

    // Ensure number fields are properly converted to numbers
    let processedValue = newValue;
    if (colDef.cellEditor === 'agNumberCellEditor') {
      if (newValue === null || newValue === undefined || newValue === '') {
        processedValue = null;
      } else {
        const numValue = typeof newValue === 'string' ? parseFloat(newValue) : newValue;
        processedValue = isNaN(numValue) ? null : numValue;
      }
    }

    // If RATING field is updated, automatically update NAIC_DESIGNATION
    // If ASSET_TYPE field is updated, automatically update COLLATERAL_TYPE
    const updatedFields = {
      [colDef.field]: processedValue
    };

    if (colDef.field === 'RATING') {
      const naicDesignation = getNaicDesignationFromRating(newValue);
      updatedFields.NAIC_DESIGNATION = naicDesignation;
    }

    if (colDef.field === 'ASSET_TYPE') {
      const collateralType = getCollateralTypeFromAssetType(newValue);
      updatedFields.COLLATERAL_TYPE = collateralType;
    }

    // Batch state updates to prevent multiple re-renders
    setRowData(currentRowData => {
      const newRowData = [...currentRowData];
      const rowIndex = renamedFrom
        ? newRowData.findIndex(row => row === data || row.CUSIP_PPN === lookupKey)
        : newRowData.findIndex(row => row.CUSIP_PPN === cusipPpn);

      if (rowIndex !== -1) {
        newRowData[rowIndex] = {
          ...newRowData[rowIndex],
          ...updatedFields
        };
      }

      return newRowData;
    });

    // Track which row was updated
    setUpdatedRows(prev => {
      const newUpdatedRows = { ...prev };
      if (renamedFrom) {
        // Always marked created, even if the placeholder had no entry: tokenFor
        // then sends a null MODIFIED_DATE, never a loaded row's token.
        newUpdatedRows[cusipPpn] = {
          ...newUpdatedRows[renamedFrom],
          created: newUpdatedRows[renamedFrom]?.created
            || { oldValue: null, newValue: 'new_row', timestamp: currentTime },
        };
        delete newUpdatedRows[renamedFrom];
      }
      if (!newUpdatedRows[cusipPpn]) {
        newUpdatedRows[cusipPpn] = {};
      }

      // Track the main field change (use the same processedValue from above)
      newUpdatedRows[cusipPpn] = {
        ...newUpdatedRows[cusipPpn],
        [colDef.field]: {
          oldValue: renamedFrom || data[colDef.field],
          newValue: processedValue,
          timestamp: currentTime
        }
      };

      // If RATING changed, also track NAIC_DESIGNATION change
      if (colDef.field === 'RATING') {
        const naicDesignation = getNaicDesignationFromRating(newValue);
        newUpdatedRows[cusipPpn].NAIC_DESIGNATION = {
          oldValue: data.NAIC_DESIGNATION,
          newValue: naicDesignation,
          timestamp: currentTime
        };
      }

      // If ASSET_TYPE changed, also track COLLATERAL_TYPE change
      if (colDef.field === 'ASSET_TYPE') {
        const collateralType = getCollateralTypeFromAssetType(newValue);
        newUpdatedRows[cusipPpn].COLLATERAL_TYPE = {
          oldValue: data.COLLATERAL_TYPE,
          newValue: collateralType,
          timestamp: currentTime
        };
      }

      return newUpdatedRows;
    });

    // Force AG-Grid to refresh the row to apply highlighting
    if (gridRef.current && gridRef.current.api) {
      const rowNode = gridRef.current.api.getRowNode(cusipPpn);
      if (rowNode) {
        gridRef.current.api.redrawRows({ rowNodes: [rowNode] });
      }
    }

  }, [showNotification]);

  const onPasteStart = useCallback(() => {
    // Paste operation started
  }, []);

  const onPasteEnd = useCallback(() => {
    showNotification('Data pasted successfully', 'success');
  }, [showNotification]);

  // Helper functions for working with updated rows dictionary
  const isRowUpdated = useCallback((secMasterId) => {
    return Object.prototype.hasOwnProperty.call(updatedRows, secMasterId);
  }, [updatedRows]);

  const getUpdatedRowCount = useCallback(() => {
    return Object.keys(updatedRows).length;
  }, [updatedRows]);

  const validateSave = useCallback(() => {
    // Only validate rows that will be saved (changed and new rows, never CRE)
    const rowsToSave = rowData.filter(row => {
      const cusipPpn = row.CUSIP_PPN;
      const rowUpdates = updatedRows[cusipPpn];
      const isNewRow = cusipPpn.startsWith('new_row_') || (rowUpdates && rowUpdates.created);
      return (isRowUpdated(cusipPpn) || isNewRow) && !isReadOnlySecMaster(row);
    });
    return validateSecMasterList(rowsToSave);
  }, [rowData, isRowUpdated, updatedRows]);

  const handleClearFilters = useCallback(() => {
    // Clear the quick filter text
    setQuickFilterText('');

    // Clear all column filters using AG-Grid API
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setFilterModel(null);
      // A user source, so core 2.0.2's useGridConfigs saves the cleared filters.
      gridRef.current.api.onFilterChanged('columnFilter');
      showNotification('All filters cleared', 'success');
    }
  }, [showNotification]);

  const handleSave = useCallback(async (e) => {
    if (writeBlockedRef.current) return false;
    e.preventDefault();

    // Get only rows that have changes or are new
    const changedRows = rowData.filter(row => {
      const cusipPpn = row.CUSIP_PPN;
      const rowUpdates = updatedRows[cusipPpn];
      const isNewRow = cusipPpn.startsWith('new_row_') || (rowUpdates && rowUpdates.created);
      return isRowUpdated(cusipPpn) || isNewRow;
    });

    if (changedRows.length === 0) {
      showNotification('No changes to save', 'info');
      return;
    }

    // CRE loans are synthesized from the CRE app and rejected by the server: never
    // send them. Their local edits are dropped and reverted by the reload.
    const readOnlyRows = changedRows.filter(isReadOnlySecMaster);
    const rowsToSave = changedRows.filter(row => !isReadOnlySecMaster(row));
    const readOnlyNote = readOnlyRows.length > 0
      ? `${READ_ONLY_MESSAGE} Not saved: ${readOnlyRows.map(row => row.CUSIP_PPN).join(', ')}`
      : '';
    const dropSavedRows = (rows) => {
      setUpdatedRows(prev => {
        const remaining = { ...prev };
        rows.forEach(row => {
          delete remaining[row.CUSIP_PPN];
        });
        return remaining;
      });
      const dropped = new Set(rows.map(row => row.CUSIP_PPN));
      setRowData(prev => prev.filter(row => !(dropped.has(row.CUSIP_PPN) && row.CUSIP_PPN.startsWith('new_row_'))));
    };

    if (rowsToSave.length === 0) {
      showNotification(readOnlyNote, 'warning');
      dropSavedRows(readOnlyRows);
      await loadSecMaster(true);
      return;
    }

    const validationErrorsList = validateSave();

    if (validationErrorsList.length !== 0) {
      console.error("Validation Error");
      setValidationErrors(validationErrorsList);
      setValidationErrorModalOpen(true);
    } else {
      try {
        setIsLoading(true); // Disable grid during save
        // Only changed rows: the route writes every row sent, each with its loaded MODIFIED_DATE
        const dataToSave = formatSecMasterPayload(rowsToSave, tokenFor, baselineFor);

        await saveSecMaster(dataToSave);
        const savedMessage = `Successfully saved ${dataToSave.length} changed entries`;
        showNotification(
          readOnlyNote ? `${savedMessage}. ${readOnlyNote}` : savedMessage,
          readOnlyNote ? 'warning' : 'success'
        );

        // Clear tracking (and drop new rows) only for rows handled by this save,
        // keep unsaved changes
        dropSavedRows(changedRows);

        // Reset new row counter since all new rows have been saved
        setNewRowCount(0);

        await loadSecMaster(true); // Refresh data after save and preserve unsaved changes
      } catch (error) {
        console.error("Error saving:", error);
        await notifySaveError(error, "Failed to save entries. Please try again.");
        setIsLoading(false); // Re-enable grid if there's an error
      }
    }
  }, [rowData, updatedRows, loadSecMaster, validateSave, showNotification, isRowUpdated, notifySaveError, tokenFor, baselineFor]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h5">Security Master Entries</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              disabled={isLoading || Boolean(conflictReview)}
              format="YYYY-MM-DD"
              sx={{ minWidth: 200 }}
            />
          </LocalizationProvider>
          {isHistorical && <Alert severity="info">Historical snapshots are read-only. Select today's date to edit the latest entries.</Alert>}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          <Tooltip title="Upload Excel (.xlsx, .xls) or CSV file to import secmaster entries">
            <span>
              <Button
                aria-label="Upload Excel (.xlsx, .xls) or CSV file to import secmaster entries"
                startIcon={<UploadIcon />}
                onClick={handleImportSecMaster}
                disabled={isLoading || editingBlocked}
              >
                Import Sec Master
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={isExporting ? "Export in progress..." : "Export current secmaster entries to Excel file"}>
            <span>
              <Button
                aria-label={isExporting ? "Export in progress..." : "Export current secmaster entries to Excel file"}
                onClick={handleExportMenuOpen}
                disabled={isExporting}
                endIcon={<ArrowDropDownIcon />}
              >
                {isExporting ? 'Exporting...' : `Export${selectedForExport.size > 0 ? ` (${selectedForExport.size})` : ''}`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Add a new security entry">
            <span>
              <Button
                aria-label="Add a new security entry"
                onClick={handleAddSecurityMenuOpen}
                disabled={isLoading || editingBlocked}
                endIcon={<ArrowDropDownIcon />}
              >
                Add Security
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={`Save ${getUpdatedRowCount() > 0 ? `${getUpdatedRowCount()} changed` : 'all'} sec master entries`}>
            <span>
              <Button
                color={getUpdatedRowCount() > 0 ? "warning" : "primary"}
                onClick={handleSave}
                disabled={isLoading || editingBlocked || getUpdatedRowCount() === 0}
              >
                {isLoading ? 'Saving...' : `Save${getUpdatedRowCount() > 0 ? ` (${getUpdatedRowCount()})` : ''}`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Clear all column filters and quick filter">
            <span>
              <Button
                aria-label="Clear all column filters and quick filter"
                startIcon={<FilterIcon />}
                onClick={handleClearFilters}
                disabled={isLoading}
              >
                Clear Filters
              </Button>
            </span>
          </Tooltip>
          <GridConfigMenu controller={gridConfigs} />
          <input
            type="file"
            ref={importFileInputRef}
            style={{ display: 'none' }}
            accept=".csv,.xlsx,.xls"
            onChange={handleImportFileChange}
          />
        </Box>
      </Box>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleExportClearwater} disabled={isExporting}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Clearwater</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleExportMLx} disabled={isExporting}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export MLx</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Security Menu */}
      <Menu
        anchorEl={addSecurityMenuAnchor}
        open={Boolean(addSecurityMenuAnchor)}
        onClose={handleAddSecurityMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('ABS');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>ABS</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('Bank Loan');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>Bank Loan</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('CORP');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>CORP</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('Mortgage Loan');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>Mortgage Loan</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('Other CMO');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>Other CMO</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('Other MTGE');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>Other MTGE</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('Preferred Stock');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>Preferred Stock</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSelectedSecurityType('Private Loan');
            setSecurityModalOpen(true);
            handleAddSecurityMenuClose();
          }}
          disabled={isLoading}
        >
          <ListItemText>Private Loan</ListItemText>
        </MenuItem>
      </Menu>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading sec master data...</Typography>
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Quick Filter..."
          value={quickFilterText}
          onChange={handleEditFilter}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            sx: {
              backgroundColor: theme.palette.background.paper,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }
          }}
        />
      </Box>
      <div className="secmaster-grid" style={{ height: "calc(90vh - 200px)", width: "100%", overflow: "hidden" }}>
        <style>{`
          .secmaster-grid .ag-cell-range-selected {
            background-color: rgba(120, 160, 240, 0.3) !important;
            border: 2px solid #3f7cac !important;
          }
          .secmaster-grid .ag-cell-range-selected-1 {
            background-color: rgba(120, 160, 240, 0.3) !important;
          }
          .secmaster-grid .ag-cell-range-selected-2 {
            background-color: rgba(120, 160, 240, 0.2) !important;
          }
          .secmaster-grid .ag-cell-range-selected-3 {
            background-color: rgba(120, 160, 240, 0.1) !important;
          }
          .secmaster-grid .ag-cell-range-selected-4 {
            background-color: rgba(120, 160, 240, 0.1) !important;
          }
          .secmaster-grid .ag-cell-focus {
            border: 2px solid #3f7cac !important;
            background-color: rgba(120, 160, 240, 0.4) !important;
          }
          /* Cell highlighting for updated rows - similar to blotter style */
          .secmaster-grid .ag-cell-updated {
            background-color: rgba(255, 165, 0, 0.2) !important;
          }
          /* Changed cells stand out more with darker color and border */
          .secmaster-grid .ag-cell-changed {
            background-color: rgba(255, 165, 0, 0.5) !important;
            border-left: 3px solid rgba(255, 140, 0, 0.8) !important;
            border-right: 2px solid rgba(255, 140, 0, 0.6) !important;
            font-weight: 600 !important;
          }
          .secmaster-grid .ag-cell-updated-new-row {
            background-color: rgba(255, 200, 100, 0.3) !important;
          }
        `}</style>
        <PamGrid
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          excelStyles={excelStyles}
          // Data-shaped getRowId signature (PamGrid wraps it as (params) => getRowId(params.data))
          getRowId={(data) => data.CUSIP_PPN}
          quickFilterText={quickFilterText}
          rowHeight={32}
          height="calc(90vh - 200px)"
          // SecMaster owns its bespoke toolbar / quick filter / footer JSX above —
          // PamGrid is used purely as the grid wrapper.
          showToolbar={false}
          showQuickFilter={false}
          showExport={false}
          showFooter={false}
          // Grid event callbacks
          onCellValueChanged={onCellValueChanged}
          onPasteStart={onPasteStart}
          onPasteEnd={onPasteEnd}
          onGridReady={gridConfigs.onGridReady}
          onFilterChanged={gridConfigs.onGridStateChanged}
          // Approved clipboard change: rely on PamGrid native Ctrl+C/Ctrl+V; toast on copy.
          onCopy={() => showNotification('Selected cells copied to clipboard', 'success')}
          // Everything else routes through the agGridProps passthrough (spread last, so it wins).
          agGridProps={{
            ...gridConfigs.stashBindings,
            loading: isExporting,
            suppressClickEdit: isLoading || editingBlocked,
            readOnlyEdit: isLoading || editingBlocked,
            rowBuffer: 10,
            autoSizeStrategy: gridOptions.autoSizeStrategy,
            copyGroupHeadersToClipboard: false,
            suppressLastEmptyLineOnPaste: true,
            clipboardDelimiter: '\t',
            suppressRowHoverHighlight: true,
            suppressColumnMoveAnimation: true,
          }}
        />
      </div>

      <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.action.hover, borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Copy/Paste Instructions:</strong> Select cells or ranges and use Ctrl+C to copy, Ctrl+V to paste.
          Works with Excel and other spreadsheet applications.
        </Typography>
      </Box>

      <Dialog open={Boolean(conflictReview)} maxWidth="lg" fullWidth aria-labelledby="conflict-title">
        <DialogTitle id="conflict-title">Resolve concurrent changes</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Another editor saved before you. Compare your pending edits with the latest values.
            Keeping your changes will replace the latest values when you press Save again.
            Using latest values discards all pending grid edits.
          </Typography>
          {conflictReview?.missing && <Alert severity="warning">
            A pending row is no longer present or has not been created. Reload the latest values before editing it again.
          </Alert>}
          <Table size="small">
            <TableHead><TableRow>
              {['CUSIP / PPN', 'Field', 'Originally loaded', 'Latest saved value', 'Your pending value'].map(label =>
                <TableCell key={label}>{label}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>{conflictReview?.comparisons.map(item => <TableRow key={`${item.key}:${item.field}`}>
              {[item.key, item.field, item.original, item.server, item.local].map((value, index) =>
                <TableCell key={index}>{value == null || value === '' ? '(Blank)' : String(value)}</TableCell>)}
            </TableRow>)}</TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resolveConflict(false)}>Use latest values</Button>
          <Button onClick={() => resolveConflict(true)} disabled={conflictReview?.missing}>Keep my changes</Button>
        </DialogActions>
      </Dialog>

      <SecurityModal
        open={securityModalOpen}
        onClose={() => {
          setSecurityModalOpen(false);
          setSelectedSecurityType(null);
          setEditingSecMaster(null);
        }}
        onSave={editingSecMaster ? handleSaveEditedSecMaster : handleSaveNewSecurity}
        securityType={selectedSecurityType}
        userId={userId}
        secMasterData={editingSecMaster}
      />

      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Sec Master History - ${selectedSecMasterForHistory?.CUSIP_PPN || selectedSecMasterForHistory?.SECURITY_DESCRIPTION || 'Entry'}`}
        maxWidth="lg"
      >
        <SecMasterHistory cusip_ppn={selectedSecMasterForHistory?.CUSIP_PPN} showTitle={false} />
      </HistoryModal>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteSecMaster}
        title="Delete Sec Master Entry"
        message={
          secMasterToDelete
            ? `Are you sure you want to delete "${secMasterToDelete.SECURITY_NAME || secMasterToDelete.CUSIP_PPN || 'this entry'}"?\n\nThis action cannot be undone.`
            : "Are you sure you want to delete this entry?"
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />

      <ValidationErrorModal
        open={validationErrorModalOpen}
        onClose={handleCloseValidationErrorModal}
        errors={validationErrors.map(error =>
          `Row ${error.row + 1} (${error.cusip || error.securityName || 'Unknown'}): Missing ${error.emptyFields.map(f => fieldToHeaderMap[f] || f).join(', ')}`
        )}
        title="Validation Errors"
        subtitle="Please fix the following validation errors before saving:"
      />
    </Box>
  );
};

export default SecMasterGrid;
